import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { analisi } from '@/model/qsadminapi/QsAdminApiModuleModel'
import ODataStore from 'devextreme/data/odata/store'
import notify from 'devextreme/ui/notify'
import { useRef } from 'react'
import {
  Column,
  DataGrid,
  StateStoring,
  DataGridTypes,
  DataGridRef,
  Pager,
  Toolbar,
  Item,
  FilterRow,
  HeaderFilter,
  ColumnChooser,
  Scrolling,
} from 'devextreme-react/data-grid'
import { RowDblClickEvent } from 'devextreme/ui/data_grid'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import DateColumn from '@/components/date-column/DateColumn'
import { gridTotalItemsLabel } from '@/constants'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { ClickEvent } from 'devextreme/ui/button'
import { TasksToCompleteCardProps } from '@/routes/home/card/Card.types'
import { Suspense } from 'react'
import { Await } from 'react-router-typesafe'
import { useScreenSize } from '@/themes/media-query'
import { StatoAnalisi } from '@/routes/analysis/AnalysisEditor.enums'
import {
  calculateAusiliariFilterExpression,
  calculateAusiliariValue,
  cellRenderAusiliari,
} from '@/routes/analysis/utils'
const AnalysisToCompleteCard = (props: TasksToCompleteCardProps) => {
  const { getCurrentEmployee } = props
  const token = useTokenRefresh()
  const gridRef = useRef<DataGridRef>(null)
  const currentScreenSize = useScreenSize()

  return (
    <>
      <Suspense>
        <Await resolve={Promise.resolve(getCurrentEmployee)}>
          {(currentEmployee) => (
            <>
              <DataGrid
                id={`dashboard-analysis-to-complete-datagrid`}
                dataSource={{
                  store: new ODataStore({
                    url: `${import.meta.env.VITE_QSADMINAPI_HOST}/analisi`,
                    key: 'id',
                    keyType: 'Int32',
                    version: 4,
                    errorHandler: (e) => {
                      console.error(e.errorDetails)
                      if (!e.errorDetails) return
                      notify(
                        {
                          message: `Errore : ${e.errorDetails?.message}`,
                          type: 'error',
                          displayTime: 5000,
                        },
                        {
                          position: 'bottom center',
                          direction: 'up-push',
                        },
                      )
                    },
                    beforeSend: ODataStoreRequestConfiguration(token),
                    deserializeDates: false,
                  }),
                  expand: ['sede($expand=azienda)', 'creatore', 'tecnici_ausiliari'],
                  filter: [
                    ['stato/id', StatoAnalisi.APERTA],
                    'and',
                    [
                      ['creatore/id', currentEmployee?.data.value[0].id],
                      'or',
                      [`tecnici_ausiliari/any(a: a/id eq ${currentEmployee?.data.value[0].id})`],
                    ],
                  ],
                  requireTotalCount: true,
                }}
                ref={gridRef}
                noDataText="Nessun' analisi da completare"
                showBorders={true}
                showColumnLines={true}
                focusedRowEnabled={true}
                columnHidingEnabled={
                  currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium
                }
                rowAlternationEnabled={true}
                wordWrapEnabled={false}
                repaintChangesOnly={true}
                width="100%"
                height={'40vh'}
                onRowDblClick={(e: RowDblClickEvent<any, any>) => {
                  if (e.rowType === 'data') window.open(`/analysis/${e.data.id}`, '_blank')
                }}
                allowColumnResizing={currentScreenSize.isLarge || currentScreenSize.isMedium}
                allowColumnReordering={currentScreenSize.isLarge || currentScreenSize.isMedium}
              >
                <Scrolling mode="virtual"></Scrolling>
                <ColumnChooser enabled={true} />
                <HeaderFilter visible={true}></HeaderFilter>
                <FilterRow visible={true}></FilterRow>
                <Pager
                  visible={true}
                  allowedPageSizes={'auto'}
                  displayMode={'compact'}
                  showPageSizeSelector={false}
                  showInfo={true}
                  showNavigationButtons={false}
                  infoText={gridTotalItemsLabel}
                />
                <StateStoring
                  enabled={true}
                  type="localStorage"
                  storageKey={'dashboard-analysis-to-complete-datagrid'}
                  savingTimeout={50}
                />
                <Column visible={false} type="buttons" alignment="left" showInColumnChooser={false}></Column>
                <Column
                  dataField={'id'}
                  caption="ID"
                  visible={false}
                  dataType="number"
                  defaultSortOrder="desc"
                ></Column>
                <Column
                  dataField="ded_Dis"
                  cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
                    const data: analisi = cellData.row.data
                    return <ObjectCodeLink url={`/analysis/${data.id}`} text={data.ded_Dis}></ObjectCodeLink>
                  }}
                  visible={true}
                  caption="CODICE"
                ></Column>
                <Column
                  dataField="sede.azienda.nome"
                  cellRender={(cellData: DataGridTypes.ColumnCellTemplateData) => {
                    const data: analisi = cellData.row.data
                    return (
                      <ObjectCodeLink
                        url={`/clients/${data.sede?.azienda?.id}`}
                        text={data.sede?.azienda?.nome}
                      ></ObjectCodeLink>
                    )
                  }}
                  caption="AZIENDA"
                ></Column>
                <Column dataField="creatore.fullname" caption="AUTORE" visible={false}></Column>
                <Column
                  name={'ausiliari'}
                  caption={'AUSILIARI'}
                  calculateFilterExpression={calculateAusiliariFilterExpression}
                  calculateCellValue={calculateAusiliariValue}
                  cellRender={cellRenderAusiliari}
                  visible={false}
                ></Column>
                {DateColumn({
                  dataField: 'creazione',
                  caption: 'CREAZIONE',
                  format: 'dd/MM/yyyy',
                  width: 'auto',
                })}
                <Column dataField="oggetto" caption="OGGETTO" allowHeaderFiltering={false} />
                <Toolbar>
                  <Item
                    location="before"
                    locateInMenu="never"
                    showText="inMenu"
                    widget="dxButton"
                    options={{
                      hint: 'Refresh',
                      text: 'Refresh',
                      icon: 'refresh',
                      onClick: (e: ClickEvent) => {
                        gridRef.current?.instance().refresh()
                      },
                      stylingMode: 'text',
                    }}
                  ></Item>
                  <Item
                    location="before"
                    locateInMenu="never"
                    showText="inMenu"
                    widget="dxButton"
                    options={{
                      hint: 'Vai ad analisi',
                      text: 'Vai ad analisi',
                      icon: 'arrowright',
                      onClick: (e: ClickEvent) => {
                        window.open('/analysis', '_blank')
                      },
                      stylingMode: 'text',
                    }}
                  ></Item>
                  <Item
                    name="columnChooserButton"
                    locateInMenu="never"
                    showText="inMenu"
                    options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
                  />
                </Toolbar>
              </DataGrid>
            </>
          )}
        </Await>
      </Suspense>
    </>
  )
}

export default AnalysisToCompleteCard
